// src/pages/Analytics/AnalyticsHub.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { BRAND_GREEN, BRAND_GOLD, BRAND_GREEN_LIGHT } from '../../theme/sucronTheme';


const BORDER = '#E5E7EB';

// UI Primitives (padrão Design System ArenaStats)
const PageShell = ({ children }) => (
  <div className="min-h-screen" style={{ backgroundColor: '#f8fafc' }}>
    <div className="max-w-7xl mx-auto px-4 lg:px-6 py-6">
      {children}
    </div>
  </div>
);

const Card = ({ children, className = '' }) => (
  <div
    className={`bg-white rounded-2xl border ${className}`}
    style={{ borderColor: BORDER, boxShadow: '0 1px 2px 0 rgba(6, 78, 59, 0.05)' }}
  >
    {children}
  </div>
);

const CardTitle = ({ children }) => (
  <h3 className="text-sm font-extrabold uppercase tracking-wider" style={{ color: BRAND_GREEN, opacity: 0.7 }}>
    {children}
  </h3>
);

const Icons = {
  Dashboard: () => (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
  Compare: () => (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
  Report: () => (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  Export: () => (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  Query: () => (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
    </svg>
  ),
  Filter: () => (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
    </svg>
  ),
};

const AnalyticsHub = () => {
  const features = [
    {
      title: 'Dashboards Interativos',
      description: 'Explore métricas em tempo real com dashboards personalizáveis e filtros avançados.',
      icon: Icons.Dashboard,
      to: '/analytics/dashboards',
      status: 'Em Desenvolvimento',
      color: 'from-blue-500 to-blue-700',
      capabilities: ['Multi-temporada', 'Filtros avançados', 'Exportação CSV']
    },
    {
      title: 'Comparativos Pro',
      description: 'Análise profunda com percentis vs liga, segmentações por fase de jogo e contexto completo.',
      icon: Icons.Compare,
      to: '/analytics/compare-pro',
      status: 'Beta',
      color: 'from-indigo-500 to-indigo-700',
      capabilities: ['Percentis', 'Multi-jogo', 'Contexto tático']
    },
    {
      title: 'Relatórios Avançados',
      description: 'Gere relatórios detalhados com insights estatísticos e visualizações profissionais.',
      icon: Icons.Report,
      to: '/analytics/reports',
      status: 'Em Breve',
      color: 'from-violet-500 to-violet-700',
      capabilities: ['PDF/Excel', 'Templates', 'Agendamento']
    },
    {
      title: 'Query Builder',
      description: 'Construa consultas personalizadas sem SQL com interface visual drag-and-drop.',
      icon: Icons.Query,
      to: '/analytics/query-builder',
      status: 'Planejado',
      color: 'from-purple-500 to-purple-700',
      capabilities: ['Visual builder', 'SQL export', 'Histórico']
    },
    {
      title: 'Exportações',
      description: 'Exporte dados brutos e processados em múltiplos formatos para análise externa.',
      icon: Icons.Export,
      to: '/analytics/exports',
      status: 'Disponível',
      color: 'from-cyan-500 to-cyan-700',
      capabilities: ['CSV/JSON/Excel', 'API access', 'Bulk export']
    },
    {
      title: 'Filtros & Segmentações',
      description: 'Aplique filtros complexos por temporada, competição, posição, fase de jogo e mais.',
      icon: Icons.Filter,
      to: '/analytics/filters',
      status: 'Beta',
      color: 'from-teal-500 to-teal-700',
      capabilities: ['Saved filters', 'Templates', 'Share filters']
    },
  ];

  return (
    <PageShell>
      {/* Hero Section */}
      <div 
        className="relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="text-center">
            <div className="flex items-center justify-center mb-6">
              <svg className="w-16 h-16 text-white opacity-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h1 className="text-4xl sm:text-5xl font-black text-white mb-4">
              Analytics Lab
            </h1>
            <p className="text-xl text-white opacity-90 max-w-3xl mx-auto font-semibold">
              O laboratório completo para análise esportiva profunda. Dashboards, comparativos avançados, 
              relatórios auditáveis e exportações personalizadas.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <div className="px-4 py-2 rounded-full text-sm font-bold" style={{ backgroundColor: BRAND_GOLD, color: BRAND_GREEN }}>
                📊 Análise Profunda
              </div>
              <div className="px-4 py-2 rounded-full bg-white bg-opacity-20 text-white text-sm font-bold">
                🎯 Decisão Estratégica
              </div>
              <div className="px-4 py-2 rounded-full bg-white bg-opacity-20 text-white text-sm font-bold">
                📈 Auditável & Exportável
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {features.map((feature, index) => (
          <Link key={index} to={feature.to} className="block group">
            <Card className="p-6 hover:shadow-md transition-all duration-300 h-full relative">
              {/* Icon & Status */}
              <div className="flex items-start justify-between mb-4">
                <div className="p-3 rounded-xl" style={{ backgroundColor: BRAND_GREEN_LIGHT }}>
                  <div style={{ color: BRAND_GREEN }}>
                    <feature.icon />
                  </div>
                </div>
                <span className="text-xs font-bold px-3 py-1 rounded-full" 
                      style={{ backgroundColor: BRAND_GREEN_LIGHT, color: BRAND_GREEN }}>
                  {feature.status}
                </span>
              </div>

              {/* Content */}
              <h3 className="text-lg font-extrabold mb-2 group-hover:scale-105 transition-transform" 
                  style={{ color: BRAND_GREEN }}>
                {feature.title}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                {feature.description}
              </p>
              
              {/* Capabilities */}
              <div className="flex flex-wrap gap-2">
                {feature.capabilities.map((cap, capIndex) => (
                  <span key={capIndex} className="px-2 py-1 rounded-md text-xs font-semibold" 
                        style={{ backgroundColor: BRAND_GREEN_LIGHT, color: BRAND_GREEN }}>
                    {cap}
                  </span>
                ))}
              </div>

              {/* Hover Border */}
              <div className="absolute inset-0 border-2 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity" 
                   style={{ borderColor: BRAND_GOLD, pointerEvents: 'none' }} />
            </Card>
          </Link>
        ))}
      </div>

      {/* Comparison Table: Analytics vs Creator */}
      <div className="mt-12">
        <h2 className="text-3xl font-black text-center mb-8" style={{ color: BRAND_GREEN }}>
          Analytics vs Creator: Qual usar?
        </h2>
        
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ backgroundColor: BRAND_GREEN }}>
                  <th className="px-6 py-4 text-left text-white font-bold">Critério</th>
                  <th className="px-6 py-4 text-left text-white font-bold">Creator (Estúdio)</th>
                  <th className="px-6 py-4 text-left text-white font-bold">Analytics (Laboratório)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-700">Objetivo</td>
                  <td className="px-6 py-4 text-sm">Criar conteúdo visual para compartilhar</td>
                  <td className="px-6 py-4 text-sm">Análise profunda para decisão estratégica</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-700">Público</td>
                  <td className="px-6 py-4 text-sm">Criadores de conteúdo, Marketing, Mídia</td>
                  <td className="px-6 py-4 text-sm">Analistas, Scouts, Diretores esportivos</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-700">Velocidade</td>
                  <td className="px-6 py-4 text-sm">⚡ Rápido (pronto para postar)</td>
                  <td className="px-6 py-4 text-sm">🔬 Profundo (auditável e completo)</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-700">Comparativos</td>
                  <td className="px-6 py-4 text-sm">Lite (visual, widgets prontos)</td>
                  <td className="px-6 py-4 text-sm">Pro (percentis, multi-temporada, filtros)</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-700">Exportação</td>
                  <td className="px-6 py-4 text-sm">PNG, MP4 (social media)</td>
                  <td className="px-6 py-4 text-sm">CSV, Excel, PDF, JSON (dados brutos)</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-700">Ideal para</td>
                  <td className="px-6 py-4 text-sm">📱 Redes sociais, Feed, Engajamento</td>
                  <td className="px-6 py-4 text-sm">📊 Relatórios, Scouting, Tomada de decisão</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* CTA Section */}
      <div className="mt-12">
        <Card className="p-12 text-center" style={{ background: `linear-gradient(135deg, ${BRAND_GREEN} 0%, #374151 100%)` }}>
          <h2 className="text-3xl font-black text-white mb-4">
            Pronto para insights profundos?
          </h2>
          <p className="text-white text-lg opacity-90 mb-8 max-w-2xl mx-auto">
            O Analytics Lab oferece as ferramentas mais avançadas para análise esportiva profissional. 
            Dashboards interativos, comparativos com percentis e exportações auditáveis.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/analytics/dashboards"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              style={{ backgroundColor: BRAND_GOLD, color: BRAND_GREEN }}
            >
              <Icons.Dashboard />
              <span>Explorar Dashboards</span>
            </Link>
            <Link
              to="/analytics/compare-pro"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 bg-white text-blue-900"
            >
              <Icons.Compare />
              <span>Comparativos Pro</span>
            </Link>
          </div>
        </Card>
      </div>
    </PageShell>
  );
};

export default AnalyticsHub;
